import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, type Observable } from 'rxjs';

import type {
    DiagnosticDto,
    DiagnosticsWithRedirectionLinkDto,
    DiagnosticWithRedirectionLinkDto,
    InstagramPageInfoDto,
    TrackDiagnosticActionBodyDto,
} from '@malou-io/package-dto';
import type { ApiResultV2, MaloupeEventName, MaloupeLocale } from '@malou-io/package-utils';

import { environment } from ':environments/environment';

@Injectable({ providedIn: 'root' })
export class DiagnosticHttpService {
    readonly API_BASE_URL = `${environment.APP_MALOU_API_URL}/api/maloupe/diagnostic`;

    private readonly _http = inject(HttpClient);

    getRestaurantInconsistencies$(diagnosticId: string): Observable<DiagnosticDto> {
        return this._http
            .put<ApiResultV2<DiagnosticDto>>(`${this.API_BASE_URL}/${diagnosticId}/inconsistencies`, {})
            .pipe(map((response) => response.data));
    }

    createDiagnostic$(placeId: string, lang: MaloupeLocale): Observable<DiagnosticDto> {
        return this._http
            .post<ApiResultV2<DiagnosticDto>>(`${this.API_BASE_URL}/${placeId}`, {
                lang,
            })
            .pipe(map((response) => response.data));
    }

    updateWithSimilarRestaurants$(diagnosticId: string): Observable<DiagnosticDto> {
        return this._http
            .put<ApiResultV2<DiagnosticDto>>(`${this.API_BASE_URL}/${diagnosticId}/similar-restaurants`, {})
            .pipe(map((response) => response.data));
    }

    updateWithGoogleDiagnostic$(diagnosticId: string): Observable<DiagnosticDto> {
        return this._http
            .put<ApiResultV2<DiagnosticDto>>(`${this.API_BASE_URL}/${diagnosticId}/google`, {})
            .pipe(map((response) => response.data));
    }

    updateWithInstagramPageInfo$(diagnosticId: string, instagramAccountName: string): Observable<DiagnosticDto> {
        return this._http
            .put<ApiResultV2<DiagnosticDto>>(`${this.API_BASE_URL}/${diagnosticId}/instagram`, { instagramAccountName })
            .pipe(map((response) => response.data));
    }

    getInstagramPage$(text: string): Observable<InstagramPageInfoDto> {
        return this._http
            .get<ApiResultV2<InstagramPageInfoDto>>(`${this.API_BASE_URL}/instagram/${text}`)
            .pipe(map((response) => response.data));
    }

    updateWithKeywords$(diagnosticId: string): Observable<DiagnosticDto> {
        return this._http
            .put<ApiResultV2<DiagnosticDto>>(`${this.API_BASE_URL}/${diagnosticId}/keywords`, {})
            .pipe(map((response) => response.data));
    }

    updateWithInstagramRating$(diagnosticId: string): Observable<DiagnosticDto> {
        return this._http
            .put<ApiResultV2<DiagnosticDto>>(`${this.API_BASE_URL}/${diagnosticId}/instagram-rating`, {})
            .pipe(map((response) => response.data));
    }

    updateWithAverageReviewCountForSimilarRestaurants$(diagnosticId: string): Observable<DiagnosticDto> {
        return this._http
            .put<ApiResultV2<DiagnosticDto>>(`${this.API_BASE_URL}/${diagnosticId}/review-count`, {})
            .pipe(map((response) => response.data));
    }

    updateRestaurantCategory$(diagnosticId: string, categoryId: string): Observable<DiagnosticDto> {
        return this._http
            .put<ApiResultV2<DiagnosticDto>>(`${this.API_BASE_URL}/${diagnosticId}/category`, { categoryId })
            .pipe(map((response) => response.data));
    }

    updateWithReviewAnalyses$(diagnosticId: string): Observable<DiagnosticDto> {
        return this._http
            .put<ApiResultV2<DiagnosticDto>>(`${this.API_BASE_URL}/${diagnosticId}/reviews-analyses`, {})
            .pipe(map((response) => response.data));
    }

    updateWithEventName$(diagnosticId: string, eventName: MaloupeEventName | null): Observable<DiagnosticDto> {
        return this._http
            .put<ApiResultV2<DiagnosticDto>>(`${this.API_BASE_URL}/${diagnosticId}/event`, { eventName })
            .pipe(map((response) => response.data));
    }

    getDiagnosticByIdWhileAuthenticated$(diagnosticId: string, jwtToken: string): Observable<DiagnosticWithRedirectionLinkDto> {
        return this._http
            .get<ApiResultV2<DiagnosticWithRedirectionLinkDto>>(`${this.API_BASE_URL}/in/${diagnosticId}`, {
                headers: {
                    Authorization: `Bearer ${jwtToken}`,
                },
            })
            .pipe(map((response) => response.data));
    }

    getDiagnosticsByIdsWhileAuthenticated$(diagnosticIds: string[], jwtToken: string): Observable<DiagnosticsWithRedirectionLinkDto> {
        return this._http
            .post<ApiResultV2<DiagnosticsWithRedirectionLinkDto>>(
                `${this.API_BASE_URL}/in/many`,
                {
                    diagnosticIds,
                },
                {
                    headers: {
                        Authorization: `Bearer ${jwtToken}`,
                    },
                }
            )
            .pipe(map((response) => response.data));
    }

    getDiagnosticById$(diagnosticId: string): Observable<DiagnosticDto> {
        return this._http.get<ApiResultV2<DiagnosticDto>>(`${this.API_BASE_URL}/${diagnosticId}`).pipe(map((response) => response.data));
    }

    getDiagnosticsByIds$(diagnosticIds: string[]): Observable<DiagnosticsWithRedirectionLinkDto> {
        return this._http
            .post<ApiResultV2<DiagnosticsWithRedirectionLinkDto>>(`${this.API_BASE_URL}/many`, {
                diagnosticIds,
            })
            .pipe(map((response) => response.data));
    }

    getDiagnosticByEncryptedId$(diagnosticId: string): Observable<DiagnosticDto> {
        return this._http
            .get<ApiResultV2<DiagnosticDto>>(`${this.API_BASE_URL}/result/${diagnosticId}`)
            .pipe(map((response) => response.data));
    }

    getDiagnosticsByEncryptedIds$(diagnosticIds: string[]): Observable<DiagnosticDto[]> {
        return this._http
            .post<ApiResultV2<DiagnosticDto[]>>(`${this.API_BASE_URL}/results`, {
                diagnosticIds,
            })
            .pipe(map((response) => response.data));
    }

    getEventDiagnostics$(event: MaloupeEventName): Observable<DiagnosticDto[]> {
        return this._http.get<ApiResultV2<DiagnosticDto[]>>(`${this.API_BASE_URL}/event/${event}`).pipe(map((response) => response.data));
    }

    getTodayDiagnostics$(): Observable<DiagnosticDto[]> {
        return this._http.get<ApiResultV2<DiagnosticDto[]>>(`${this.API_BASE_URL}/today`).pipe(map((response) => response.data));
    }

    trackDiagnosticAction$(payload: TrackDiagnosticActionBodyDto): Observable<void> {
        return this._http.put<void>(`${this.API_BASE_URL}/track`, { ...payload });
    }

    getDiagnosticShareUrl$(diagnosticIds: string[]): Observable<string> {
        return this._http
            .post<ApiResultV2<{ publicDiagnosticUrl: string }>>(`${this.API_BASE_URL}/share-url`, { diagnosticIds })
            .pipe(map((response) => response.data.publicDiagnosticUrl));
    }
}
